<template>
  <new-modal-dialog
    scrollable
    :title="title"
    max-width="1100"
  >
    <!-- ========================= -->
    <!-- WELCOME MESSAGE MODE -->
    <!-- ========================= -->
    <template v-if="mode === 'welcome'">
      <v-row
        v-for="lang in languages"
        :key="lang"
        class="mb-3"
      >
        <v-col
          cols="12"
          class="py-0 my-0"
        >
          <v-text-field
            v-model="welcomeModel[lang]"
            :label="lang.toUpperCase()"
            dense
            placeholder="(undef)"
            persistent-placeholder
            outlined
            hide-details
          />
        </v-col>
      </v-row>
    </template>

    <!-- ========================= -->
    <!-- TRANSLATION OVERRIDES MODE -->
    <!-- ========================= -->
    <template v-else>
      <v-btn
        outlined
        text
        class="mb-4"
        @click="addOverride"
      >
        + Add Override
      </v-btn>

      <v-card
        v-for="(row, index) in overrideRows"
        :key="row.id"
        class="mb-4 pa-3"
        outlined
      >
        <v-row align="center">
          <v-col
            cols="12"
            class="d-flex flex-row"
          >
            <v-text-field
              v-model="row.key"
              label="Dot Notation Key"
              outlined
              dense
            />
            <v-btn
              outlined
              class="ml-2"
              color="error"
              @click="removeOverride(index)"
            >
              <v-icon small>
                mdi-delete
              </v-icon>
            </v-btn>
          </v-col>

          <v-col
            v-for="lang in languages"
            :key="lang"
            cols="6"
            class="py-0 my-0"
          >
            <v-text-field
              v-model="row.values[lang]"
              class="my-2"
              :label="lang.toUpperCase()"
              hide-details
              placeholder="(undef)"
              persistent-placeholder
              outlined
              dense
            />
          </v-col>
        </v-row>
      </v-card>
    </template>

    <template #actions>
      <v-spacer />
      <v-btn
        color="primary"
        @click="save"
      >
        Save
      </v-btn>
      <v-btn
        text
        @click="emit('close')"
      >
        Cancel
      </v-btn>
    </template>
  </new-modal-dialog>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import NewModalDialog from '@/components/dialogs/NewModalDialog.vue';

const props = defineProps({
  value: { type: Object, default: () => ({}) },
  mode: { type: String, required: true },
  languages: { type: Array, required: true },
});

const emit = defineEmits(['input', 'close']);

/* ----------------------- */
/* State */
/* ----------------------- */
const clone = (val) => JSON.parse(JSON.stringify(val || {}));

const parse = (val) => {
  if (typeof val === 'string') {
    try {
      return JSON.parse(val);
    } catch {
      return {};
    }
  }
  return val || {};
};

const welcomeModel = ref({});

const overrideRows = ref([]);

const generateId = () => Math.random().toString(36).substr(2, 9);

/* ----------------------- */
/* Welcome Mode */
/* ----------------------- */

const loadWelcome = (val) => {
  const copy = clone(val);
  const model = {};

  props.languages.forEach((lang) => {
    model[lang] = copy[lang] || '';
  });

  welcomeModel.value = model;
};

/* ----------------------- */
/* Translation Mode */
/* ----------------------- */

const normalizeValues = (values) => {
  const result = {};
  props.languages.forEach((lang) => {
    result[lang] = values?.[lang] || '';
  });
  return result;
};

const flatten = (obj, lang, prefix = '') => {
  const result = {};

  Object.entries(obj || {}).forEach(([key, val]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const nested = flatten(val, lang, fullKey);
      Object.assign(result, nested);
    } else {
      result[fullKey] = {
        key: fullKey,
        values: {
          [lang]: val,
        },
      };
    }
  });

  return result;
};

const loadOverrides = (val) => {
  const flatMap = {};

  Object.entries(val || {}).forEach(([lang, localeObj]) => {
    const flattened = flatten(localeObj, lang);

    Object.entries(flattened).forEach(([key, row]) => {
      if (!flatMap[key]) {
        flatMap[key] = {
          key,
          values: {},
        };
      }

      flatMap[key].values[lang] = row.values[lang];
    });
  });

  overrideRows.value = Object.values(flatMap).map((row) => ({
    id: generateId(),
    key: row.key,
    values: normalizeValues(row.values),
  }));
};

const expandOverrides = (rows) => {
  const result = {};

  rows.forEach((row) => {
    if (!row.key) return;

    const path = row.key.split('.');

    props.languages.forEach((lang) => {
      const value = row.values[lang];
      if (!value) return;

      // create locale only when needed
      if (!result[lang]) {
        result[lang] = {};
      }

      let cursor = result[lang];

      path.forEach((segment, index) => {
        if (index === path.length - 1) {
          cursor[segment] = value;
        } else {
          if (!cursor[segment]) {
            cursor[segment] = {};
          }
          cursor = cursor[segment];
        }
      });
    });
  });

  return result;
};

const addOverride = () => {
  const values = {};
  props.languages.forEach((lang) => {
    values[lang] = '';
  });

  overrideRows.value.push({
    id: generateId(),
    key: '',
    values,
  });
};

const removeOverride = (index) => {
  overrideRows.value.splice(index, 1);
};

const moveUp = (index) => {
  if (index === 0) return;
  const item = overrideRows.value.splice(index, 1)[0];
  overrideRows.value.splice(index - 1, 0, item);
};

const moveDown = (index) => {
  if (index === overrideRows.value.length - 1) return;
  const item = overrideRows.value.splice(index, 1)[0];
  overrideRows.value.splice(index + 1, 0, item);
};

watch(
  () => props.value,
  (val) => {
    const parsed = parse(val);

    if (props.mode === 'welcome') {
      loadWelcome(parsed);
    } else {
      loadOverrides(parsed);
    }
  },
  { immediate: true },
);

const save = () => {
  if (props.mode === 'welcome') {
    emit('input', JSON.stringify(welcomeModel.value));
  } else {
    const expanded = JSON.stringify(expandOverrides(overrideRows.value));
    emit('input', expanded);
  }

  emit('close');
};

const title = computed(() => (props.mode === 'welcome'
  ? 'Edit Welcome Message'
  : 'Edit Translation Overrides'));
</script>
